/**
 * §I Channel Rules — the section that had no editing surface anywhere in the
 * product. The backend has scored it since P1 (`bb_chunks WHERE section='I'`,
 * 3 pts, a pure "≥1 live row exists" boolean) but nothing could write a row, so
 * those 3 points were unreachable by construction.
 *
 * Field groups come from the spec's §3.2 "I — Channel Rules": approved headline
 * bank by channel, CTA preferences, voice-matched review-response templates, and
 * escalation triggers ("legal threat, safety claim → human immediately, never
 * auto-respond"). Each entry is one chunk — one rule per row, the same
 * one-fact-per-chunk discipline the voice lane uses.
 *
 * `landing_page_pattern_ref` and `signature` are spec-normative for §I but are
 * not in this panel's group list: adding a named group would invent UI for a
 * kind the product has not contractually shipped (section contract delta 3.1). They
 * are NOT invisible, though — the file-drop reader can already write both, so
 * any §I row whose kind has no group falls through to "Other channel rules"
 * below. Nothing stored in section I is unreachable from this panel.
 *
 * EXPLICIT SAVE (bb-phase2-explicit-save-brief-20260810). Section I's former
 * immediate-persistence contract is retired: adding a rule, editing a channel
 * tag and removing a rule now BUFFER in section-local state and commit through
 * the existing store actions when the operator presses Save I. Discard restores
 * from store state — the buffers are the only unsaved truth.
 *
 * Verify and unverify stay IMMEDIATE. They are operator acts of governance, not
 * content edits, and they write an append-only audit trail; a staged verify
 * could not be discarded because the trail cannot be rewound.
 *
 * Buffered ops are ID-KEYED and are never synthesised from a list diff. §B lost
 * a live prod row to a whole-list save flushing a stale optimistic delete; a
 * removal here is only ever an explicit id the operator clicked Trash on, and
 * an add only ever becomes a create.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Trash2, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { BbEntry, BbTable, ChunkKind } from '@/features/brand-bible-wizard/types';
import { VerificationRow, inputClass } from '@/components/brand-bible-wizard/primitives';
import { useRegisterBbSectionSave, type DirtyFormSaveResult } from './save';

/** One buffered "add a rule", not yet sent. No server id until Save I. */
interface PendingAdd {
  localId: string;
  kind: ChunkKind;
  body: string;
}

let pendingAddSeq = 0;
const nextPendingAddId = () => `pending-i-${++pendingAddSeq}`;

/**
 * Header "Verify all" affordance (v2 fold-in — bb-verify-all-header-brief-
 * 20260810). Same contract as SectionVoice/SectionService/SectionProof's
 * copy: sequential store calls, rank-2 outline style, hidden with nothing
 * pending or when the viewer can't verify (§4.3).
 */
type VerifyAllItem = { table: BbTable; id: string };
type VerifyAllPhase = 'idle' | 'running' | 'success' | 'partial';

function VerifyAllButton({
  items,
  canVerify,
  verifyEntry,
  tenantId,
}: {
  items: VerifyAllItem[];
  canVerify: boolean;
  verifyEntry: (table: BbTable, id: string, approveForPublic: boolean | undefined, tenantId: string) => Promise<void>;
  /** The tenant this button rendered for (a stale run is refused by the store). */
  tenantId: string;
}) {
  const [phase, setPhase] = useState<VerifyAllPhase>('idle');
  const [result, setResult] = useState<{ verified: number; failed: number } | null>(null);
  const pendingCount = items.length;

  useEffect(() => {
    if (phase === 'success' && pendingCount > 0) {
      setPhase('idle');
      setResult(null);
    }
  }, [phase, pendingCount]);

  if (!canVerify) return null;
  if (phase === 'idle' && pendingCount === 0) return null;

  const runAll = async () => {
    setPhase('running');
    let verified = 0;
    let failed = 0;
    for (const item of items) {
      try {
        await verifyEntry(item.table, item.id, undefined, tenantId);
        verified += 1;
      } catch {
        failed += 1;
      }
    }
    setResult({ verified, failed });
    setPhase(failed > 0 ? 'partial' : 'success');
  };

  if (phase === 'success') {
    return <span className="text-xs font-medium text-[hsl(var(--status-success))]">✓ All verified</span>;
  }

  if (phase === 'partial' && result) {
    return (
      <button
        type="button"
        onClick={() => void runAll()}
        className="flex-none rounded-md border border-destructive px-2.5 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10"
      >
        {result.verified} verified · {result.failed} failed — retry
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void runAll()}
      disabled={phase === 'running'}
      className="flex-none rounded-md border border-coral-dark bg-warm-bg px-2.5 py-1 text-xs font-semibold text-coral-dark hover:bg-coral-light disabled:cursor-not-allowed disabled:opacity-60"
    >
      {phase === 'running' ? 'Verifying…' : `✓ Verify all · ${pendingCount} pending`}
    </button>
  );
}

/** The four §I chunk kinds that exist in the bb_chunks vocabulary. */
const GROUPS: {
  kind: ChunkKind;
  title: string;
  hint: string;
  placeholder: string;
  channelled: boolean;
}[] = [
  {
    kind: 'headline',
    title: 'Approved headline bank',
    hint: 'headlines agents may run, tagged by channel',
    placeholder: "e.g. an approved headline, in the client's own words",
    channelled: true,
  },
  {
    kind: 'cta_preference',
    title: 'CTA preferences',
    hint: 'the calls to action this client wants, in their words',
    placeholder: 'e.g. the preferred call to action, and any wording to avoid',
    channelled: true,
  },
  {
    kind: 'review_response_template',
    title: 'Review-response templates',
    hint: 'voice-matched replies agents may adapt',
    placeholder: "e.g. a reply in the client's voice — {name} fills in at send time",
    channelled: false,
  },
  {
    kind: 'escalation_trigger',
    title: 'Escalation triggers',
    hint: 'what must go to a human immediately — never auto-answered',
    placeholder: 'e.g. a situation that must reach a human before any reply',
    channelled: false,
  },
];

/** Kinds this panel gives a named group. Anything else is an "other" rule. */
const GROUPED_KINDS = new Set<string>(GROUPS.map((g) => g.kind));

/**
 * A row's channel tag: a chip at rest, an input once clicked. The resting state
 * has to read as a value rather than an empty form field — an always-open input
 * on every row made an untagged headline look identical to a tagged one.
 *
 * The vocabulary is deliberately open. Channel stays free text and the product
 * ships no built-in channel list; anything resembling suggestions must come from
 * values this tenant already stored.
 */
function ChannelTag({
  channel,
  onCommit,
}: {
  channel: string | null;
  onCommit: (next: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <input
        autoFocus
        className="w-[120px] rounded border border-input bg-warm-bg px-1.5 py-0.5 text-[10.5px]"
        defaultValue={channel ?? ''}
        placeholder="channel (optional)"
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        onBlur={(e) => {
          const next = e.target.value.trim();
          setEditing(false);
          if (next === (channel ?? '')) return;
          // `null`, never `undefined`: JSON.stringify drops an undefined value,
          // so a PATCH body of `{}` would leave the server row untouched —
          // the channel would look cleared here but silently return on save.
          onCommit(next || null);
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      aria-label={channel ? `Change channel ${channel}` : 'Add a channel'}
      className={cn(
        'rounded-[5px] px-1.5 py-0.5 text-[10.5px]',
        channel
          ? 'bg-coral-light font-semibold text-coral-dark'
          : 'border border-dashed border-input text-muted-foreground',
      )}
    >
      {channel ?? 'channel'}
    </button>
  );
}

/** One saved rule, shared by named groups and the unknown-kind fallback. */
function RuleRow({
  row,
  meta,
  canVerify,
  onVerify,
  onUnverify,
  onRemove,
  onUndoRemove,
  pendingRemoval,
  removeLabel,
}: {
  row: BbEntry<'chunks'>;
  meta?: ReactNode;
  canVerify: boolean;
  onVerify: () => void;
  onUnverify: () => void;
  onRemove: () => void;
  onUndoRemove: () => void;
  /** Staged for delete: greyed, struck through, Trash swapped for Undo. The
   *  row only leaves on Save I, so the operator can still change their mind. */
  pendingRemoval: boolean;
  removeLabel: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-lg border border-border bg-card px-3 py-2',
        pendingRemoval && 'opacity-50',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className={cn('text-[13px] leading-snug', pendingRemoval && 'line-through')}>{row.body}</p>
        <div className="mt-1 flex items-center gap-2">
          {pendingRemoval ? (
            <span className="text-[11px] font-medium text-destructive">Removed — saves on Save I</span>
          ) : (
            <>
              {meta}
              <VerificationRow
                verifiedBy={row.verified_by}
                verifiedAt={row.verified_at}
                canVerify={canVerify}
                onVerify={onVerify}
              />
              {row.verified_by && canVerify && (
                <button
                  type="button"
                  onClick={onUnverify}
                  className="text-[11px] font-medium text-muted-foreground underline-offset-2 hover:text-coral-dark hover:underline"
                >
                  Unverify
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {pendingRemoval ? (
        <button
          type="button"
          aria-label={`Undo remove ${row.body}`}
          onClick={onUndoRemove}
          className="mt-0.5 flex-none text-muted-foreground hover:text-coral-dark"
        >
          <Undo2 className="size-3.5" />
        </button>
      ) : (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className="mt-0.5 flex-none text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/** A buffered add, shown in its group until Save I turns it into a real row. */
function PendingAddRow({ body, onUnstage }: { body: string; onUnstage: () => void }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-dashed border-coral/50 bg-card px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="text-[13px] leading-snug">{body}</p>
        <span className="mt-1 block text-[11px] font-medium text-coral-dark">New — saves on Save I</span>
      </div>
      <button
        type="button"
        aria-label={`Discard new entry ${body}`}
        onClick={onUnstage}
        className="mt-0.5 flex-none text-muted-foreground hover:text-destructive"
      >
        <Trash2 className="size-3.5" />
      </button>
    </div>
  );
}

export function ChannelRulesPanel({ canVerify }: { canVerify: boolean }) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const upsertEntry = useBrandBibleWizardStore((s) => s.upsertEntry);
  const verifyEntry = useBrandBibleWizardStore((s) => s.verifyEntry);
  const unverifyEntry = useBrandBibleWizardStore((s) => s.unverifyEntry);
  const discardEntry = useBrandBibleWizardStore((s) => s.discardEntry);
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';
  const saving = useBrandBibleWizardStore((s) => s.saving);

  // Explicit-save buffers. All three are id-keyed (or local-id keyed for adds);
  // none is ever derived by diffing the rendered list against the store.
  const [pendingAdds, setPendingAdds] = useState<PendingAdd[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<ReadonlySet<string>>(new Set());
  const [pendingChannelEdits, setPendingChannelEdits] = useState<Record<string, string | null>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const chunks = useBrandBibleWizardStore(selectEntries('chunks')).filter(
    (c) => c.deleted_at === null && c.section === 'I',
  );
  const byKind = (kind: ChunkKind): BbEntry<'chunks'>[] => chunks.filter((c) => c.kind === kind);
  const ungrouped = chunks.filter((c) => !GROUPED_KINDS.has(c.kind ?? ''));
  // Verify stays immediate, so its queue is the stored rows only — a buffered
  // add has no id yet and cannot be verified before it exists.
  const pendingChannelItems: VerifyAllItem[] = chunks
    .filter((c) => !c.verified_by)
    .map((c) => ({ table: 'chunks' as const, id: c.id }));

  const channelFor = (row: BbEntry<'chunks'>): string | null =>
    row.id in pendingChannelEdits ? pendingChannelEdits[row.id] : (row.channel ?? null);

  // What the operator can actually see right now: stored rows still standing,
  // plus buffered adds. A row staged for removal still renders (struck through)
  // so it stays counted until Save I actually deletes it.
  const visibleCount = chunks.length + pendingAdds.length;
  // R61 S2: while the panel is collapsed, drafts waiting for review (e.g. from the client questionnaire) are listed
  // with their Verify control, so a draft is never hidden behind a closed panel.
  const collapsedDrafts = open ? [] : chunks.filter((c) => !c.verified_by);

  const stageAdd = (kind: ChunkKind) => {
    const body = (drafts[kind] ?? '').trim();
    if (!body) return;
    setPendingAdds((p) => [...p, { localId: nextPendingAddId(), kind, body }]);
    setDrafts((d) => ({ ...d, [kind]: '' }));
    setErrors((e) => ({ ...e, [kind]: '' }));
  };
  const unstageAdd = (localId: string) =>
    setPendingAdds((p) => p.filter((a) => a.localId !== localId));

  const stageRemoval = (id: string) => setPendingRemovals((s) => new Set(s).add(id));
  const undoRemoval = (id: string) =>
    setPendingRemovals((s) => {
      if (!s.has(id)) return s;
      const next = new Set(s);
      next.delete(id);
      return next;
    });

  const stageChannelEdit = (id: string, next: string | null) =>
    setPendingChannelEdits((m) => ({ ...m, [id]: next }));

  // Drives registration identity below: when this moves, §I re-registers and
  // the registry notifies the Save button / Unsaved indicator.
  const totalChangeCount =
    pendingAdds.length + pendingRemovals.size + Object.keys(pendingChannelEdits).length;

  // The registration must be referentially stable — the registry re-registers
  // whenever the object identity changes. Callbacks read the live buffers
  // through a ref rather than closing over a render's snapshot.
  const buffers = useRef({ pendingAdds, pendingRemovals, pendingChannelEdits });
  buffers.current = { pendingAdds, pendingRemovals, pendingChannelEdits };

  const registration = useMemo(
    () => ({
      isDirty: () => {
        const b = buffers.current;
        return (
          b.pendingAdds.length > 0 ||
          b.pendingRemovals.size > 0 ||
          Object.keys(b.pendingChannelEdits).length > 0
        );
      },
      changeCount: () => {
        const b = buffers.current;
        return b.pendingAdds.length + b.pendingRemovals.size + Object.keys(b.pendingChannelEdits).length;
      },
      discard: () => {
        setPendingAdds([]);
        setPendingRemovals(new Set());
        setPendingChannelEdits({});
        setSaveError(null);
      },
      save: async (): Promise<DirtyFormSaveResult> => {
        const { pendingAdds: adds, pendingRemovals: removals, pendingChannelEdits: edits } =
          buffers.current;
        setSaveError(null);

        // Sequential, and each op is retired from its buffer the moment it
        // succeeds. A partial failure must never re-send a committed op on the
        // next Save — that is how a retry turns one rule into two.
        const failedAdds: PendingAdd[] = [];
        for (const a of adds) {
          try {
            await upsertEntry('chunks', { section: 'I', kind: a.kind, body: a.body }, undefined, bbTenant);
            setPendingAdds((p) => p.filter((x) => x.localId !== a.localId));
          } catch {
            failedAdds.push(a);
          }
        }

        const failedEdits: string[] = [];
        for (const [id, channel] of Object.entries(edits)) {
          try {
            await upsertEntry('chunks', { channel }, id, bbTenant);
            setPendingChannelEdits((m) => {
              const next = { ...m };
              delete next[id];
              return next;
            });
          } catch {
            failedEdits.push(id);
          }
        }

        const failedRemovals: string[] = [];
        for (const id of removals) {
          try {
            await discardEntry('chunks', id, bbTenant);
            setPendingRemovals((s) => {
              const next = new Set(s);
              next.delete(id);
              return next;
            });
          } catch {
            failedRemovals.push(id);
          }
        }

        const failures = failedAdds.length + failedEdits.length + failedRemovals.length;
        if (failures > 0) {
          const message = `Couldn't save ${failures} channel rule change${failures === 1 ? '' : 's'} — try again.`;
          setSaveError(message);
          return { success: false, error: message };
        }
        return true;
      },
    }),
    // Re-create the registration whenever the buffered change count moves.
    // Re-registering is what calls the registry's notify(), and notify() is the
    // only thing that re-runs the Save button's and Unsaved pill's
    // useSyncExternalStore selectors. With a stable dep list §I registered once
    // and never announced itself again: buffering worked, the preview row and
    // the header count updated, and "Save I" stayed DISABLED forever — the
    // section's Save path was unreachable on prod. Caught by the live save
    // probe, 2026-08-17. The callbacks read live state through `buffers`, so a
    // narrow dep list never makes them stale. Same shape §G uses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [totalChangeCount],
  );

  useRegisterBbSectionSave('I', registration);

  return (
    <div className="col-span-2 rounded-lg border border-border bg-warm-bg">
      {/* Split from a single all-encompassing toggle button into a toggle +
          trailing action row: the header now also carries the Verify-all
          affordance, and a button can't nest inside another button (invalid
          HTML, and the outer onClick would double-fire on every click). */}
      <div className="flex w-full items-center gap-2 px-3.5 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {open ? (
            <ChevronDown className="size-3.5 flex-none text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 flex-none text-muted-foreground" />
          )}
          <span className="text-[12.5px] font-semibold">Channel rules</span>
          <span className="text-[11px] text-muted-foreground">
            headlines · CTAs · review replies · escalation
          </span>
        </button>
        <span className="flex-none rounded-[5px] bg-teal-light px-1.5 py-0.5 font-mono text-[10.5px] text-teal">
          {visibleCount} {visibleCount === 1 ? 'rule' : 'rules'}
        </span>
        <VerifyAllButton items={pendingChannelItems} canVerify={canVerify} verifyEntry={verifyEntry} tenantId={bbTenant} />
      </div>

      {saveError && (
        <p role="alert" className="mx-3.5 mb-2 text-[11px] text-destructive">
          {saveError}
        </p>
      )}

      {collapsedDrafts.length > 0 && (
        <div data-testid="channel-rules-drafts" className="flex flex-col gap-1.5 border-t border-border px-3.5 py-2.5">
          <span className="text-[11px] font-semibold text-muted-foreground">Drafts to review</span>
          {collapsedDrafts.map((row) => (
            <div key={row.id} className="flex flex-wrap items-center gap-2 text-[12.5px]">
              <span>{row.body}</span>
              <VerificationRow
                verifiedBy={row.verified_by}
                verifiedAt={row.verified_at}
                canVerify={canVerify}
                onVerify={() => void verifyEntry('chunks', row.id, undefined, bbTenant)}
              />
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="flex flex-col gap-4 border-t border-border p-3.5">
          {GROUPS.map((group) => {
            const rows = byKind(group.kind);
            return (
              <div key={group.kind}>
                <div className="mb-1.5 flex items-baseline gap-2">
                  <span className="text-[12.5px] font-semibold">{group.title}</span>
                  <span className="text-[11px] text-muted-foreground">{group.hint}</span>
                </div>

                <div className="flex flex-col gap-1.5">
                  {rows.map((row) => (
                    <RuleRow
                      key={row.id}
                      row={row}
                      canVerify={canVerify}
                      onVerify={() => void verifyEntry('chunks', row.id, undefined, bbTenant)}
                      onUnverify={() => void unverifyEntry('chunks', row.id, bbTenant)}
                      onRemove={() => stageRemoval(row.id)}
                      onUndoRemove={() => undoRemoval(row.id)}
                      pendingRemoval={pendingRemovals.has(row.id)}
                      removeLabel={`Remove ${group.title} entry`}
                      meta={
                        group.channelled ? (
                          <ChannelTag
                            channel={channelFor(row)}
                            onCommit={(next) => stageChannelEdit(row.id, next)}
                          />
                        ) : null
                      }
                    />
                  ))}

                  {pendingAdds
                    .filter((a) => a.kind === group.kind)
                    .map((a) => (
                      <PendingAddRow
                        key={a.localId}
                        body={a.body}
                        onUnstage={() => unstageAdd(a.localId)}
                      />
                    ))}

                  <div className="flex items-center gap-2">
                    <input
                      className={cn(inputClass, 'text-[13px]')}
                      value={drafts[group.kind] ?? ''}
                      placeholder={group.placeholder}
                      onChange={(e) => setDrafts((d) => ({ ...d, [group.kind]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          stageAdd(group.kind);
                        }
                      }}
                    />
                    <button
                      type="button"
                      disabled={saving || !(drafts[group.kind] ?? '').trim()}
                      onClick={() => stageAdd(group.kind)}
                      className="flex-none rounded-lg border border-dashed border-input px-3 py-2 text-xs font-medium text-teal hover:border-coral hover:text-coral-dark disabled:opacity-40"
                    >
                      Add
                    </button>
                  </div>
                  {errors[group.kind] && (
                    <p role="alert" className="text-[11px] text-destructive">
                      {errors[group.kind]}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {ungrouped.length > 0 && (
            <div>
              <div className="mb-1.5 flex items-baseline gap-2">
                <span className="text-[12.5px] font-semibold">Other channel rules</span>
                <span className="text-[11px] text-muted-foreground">
                  saved by another tool — no editor here yet, but they still apply
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                {ungrouped.map((row) => (
                  <RuleRow
                    key={row.id}
                    row={row}
                    canVerify={canVerify}
                    onVerify={() => void verifyEntry('chunks', row.id, undefined, bbTenant)}
                    onUnverify={() => void unverifyEntry('chunks', row.id, bbTenant)}
                    onRemove={() => stageRemoval(row.id)}
                    onUndoRemove={() => undoRemoval(row.id)}
                    pendingRemoval={pendingRemovals.has(row.id)}
                    removeLabel={`Remove ${row.kind ?? 'channel rule'} entry`}
                    meta={
                      <span className="rounded-[5px] bg-teal-light px-1.5 py-0.5 font-mono text-[10.5px] text-teal">
                        {row.kind ?? 'unknown'}
                      </span>
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
